import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Shield, CheckCircle, AlertCircle, Loader2, Mail, Clock } from 'lucide-react';
import { authAPI } from '../../services/api';
import { toast } from 'sonner';
import '../../styles/vds-auth.css';

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [status, setStatus] = useState('loading'); // loading, success, expired, error
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [resending, setResending] = useState(false);
  
  // Prevent double API calls in React StrictMode
  const verificationAttempted = useRef(false);

  const verifyEmail = useCallback(async () => {
    if (!token) return;
    
    try {
      const res = await authAPI.verifyEmail(token);
      setEmail(res.data.email);
      setStatus('success');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Verification failed';
      if (detail.toLowerCase().includes('expired')) {
        setStatus('expired');
        setError(detail);
      } else {
        setStatus('error');
        setError(detail);
      }
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setError('No verification token provided');
      return;
    }
    
    // Prevent double calls from React StrictMode
    if (verificationAttempted.current) {
      return;
    }
    verificationAttempted.current = true;
    
    verifyEmail();
  }, [token, verifyEmail]);

  const handleResendVerification = async () => {
    setResending(true);
    try {
      // Need to be logged in to resend - redirect to login with message
      toast.info('Please log in to request a new verification email');
      navigate('/login?resend_verification=true');
    } catch (err) {
      toast.error('Failed to process request');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="vds-auth-page">
      <div className="vds-auth-container">
        <div className="vds-auth-header">
          <Link to="/" className="vds-auth-logo">
            <div className="vds-auth-logo-mark">
              <Shield className="h-5 w-5" />
            </div>
            <span className="vds-auth-logo-text">VETT</span>
          </Link>
        </div>

        <div className="vds-auth-card">
          {status === 'loading' && (
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin" style={{ color: '#1B5AFF' }} />
              <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#0A0F1E', fontFamily: "'Bricolage Grotesque', sans-serif", marginBottom: '8px' }}>
                Verifying your email...
              </h2>
              <p style={{ color: '#5B6472' }}>Please wait a moment</p>
            </div>
          )}

          {status === 'success' && (
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'rgba(0, 201, 167, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
                <CheckCircle className="w-8 h-8" style={{ color: '#00C9A7' }} />
              </div>
              <h2 style={{ fontSize: '24px', fontWeight: 700, color: '#0A0F1E', fontFamily: "'Bricolage Grotesque', sans-serif", marginBottom: '8px' }}>
                Email Verified!
              </h2>
              <p style={{ color: '#5B6472', marginBottom: '24px' }}>
                Your email {email && <span style={{ color: '#0A0F1E' }}>{email}</span>} has been verified successfully. You now have full access to VETT.
              </p>
              <button
                onClick={() => navigate('/login')}
                className="vds-btn-auth-primary"
                data-testid="continue-to-login-btn"
              >
                Continue to Login
              </button>
            </div>
          )}

          {status === 'expired' && (
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'rgba(240, 180, 41, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
                <Clock className="w-8 h-8" style={{ color: '#F0B429' }} />
              </div>
              <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#0A0F1E', fontFamily: "'Bricolage Grotesque', sans-serif", marginBottom: '8px' }}>
                Link Expired
              </h2>
              <p style={{ color: '#5B6472', marginBottom: '24px' }}>
                This verification link has expired. Verification links are valid for 24 hours.
              </p>
              <button
                onClick={handleResendVerification}
                disabled={resending}
                className="vds-btn-auth-primary"
                style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                data-testid="request-new-link-btn"
              >
                {resending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4" />
                    Request New Verification Link
                  </>
                )}
              </button>
              <p style={{ color: '#5B6472', fontSize: '14px' }}>
                You'll need to log in first to request a new link.
              </p>
            </div>
          )}

          {status === 'error' && (
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'rgba(229, 72, 77, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
                <AlertCircle className="w-8 h-8" style={{ color: '#E5484D' }} />
              </div>
              <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#0A0F1E', fontFamily: "'Bricolage Grotesque', sans-serif", marginBottom: '8px' }}>
                Verification Failed
              </h2>
              <p style={{ color: '#5B6472', marginBottom: '16px' }}>{error}</p>
              <p style={{ color: '#5B6472', fontSize: '14px', marginBottom: '24px' }}>
                The link may have already been used or is invalid. Please log in and request a new verification email from your dashboard.
              </p>
              <Link to="/login">
                <button className="vds-btn-auth-primary">
                  Go to Login
                </button>
              </Link>
            </div>
          )}
        </div>

        <div className="vds-auth-footer">
          <p className="vds-auth-footer-text">
            Need help? <Link to="/contact">Contact Support</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
